/** Create or edit a staff profile (ST-01 + admin-web rework payroll/HR).
 *  A centered <Modal> popup — the SAME add/edit pattern as members and products
 *  (NewMemberModal / ProductFormModal): title, scrollable field body, and a
 *  Cancel/Save footer. NOT an inline expandable panel.
 *  Create: staff == null → useCreateStaff. Edit: staff != null → useUpdateStaff.
 *  Client-side rule: a staff member must have email or phone.
 *  Salaries are stored as integer minor units (ден×100); the form shows/accepts
 *  major units (ден) and converts at the edge. Fields are grouped into sections:
 *  Личност · Вработување · Плата · Белешки. */
import type { Role } from '@gym/shared';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/Button.js';
import { Field, Input, Select, Textarea } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';

import { type StaffRow, useCreateStaff, useUpdateStaff } from './hooks/useStaff.js';

const ROLES = ['owner', 'manager', 'front_desk', 'trainer'] as const;
const EMPLOYMENT_TYPES = ['full_time', 'part_time'] as const;
const LOCALES = ['mk', 'sq', 'en'] as const;

/** minor units (ден×100) → major-unit string for an input field. */
function toMajor(minor: number | null | undefined): string {
  if (minor == null) return '';
  return String(minor / 100);
}

/** major-unit input string → minor units, or null when empty/invalid. */
function toMinor(majorStr: string): number | null {
  const s = majorStr.trim();
  if (!s) return null;
  const v = Number(s);
  if (Number.isNaN(v)) return null;
  return Math.round(v * 100);
}

export function StaffForm({
  open = true,
  onOpenChange,
  staff,
  onSaved,
}: {
  /** Controls the modal. Defaults to open (keeps the form usable in tests). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** null / undefined → create mode; a staff row → edit mode */
  staff?: StaffRow | null;
  /** Called after a successful save (in addition to closing the modal). */
  onSaved?: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const isEdit = staff != null;
  const create = useCreateStaff();
  const update = useUpdateStaff();
  const pending = create.isPending || update.isPending;

  // ── Личност ─────────────────────────────────────────────────────────────────
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [nationalId, setNationalId] = useState('');
  const [address, setAddress] = useState('');
  const [emergencyName, setEmergencyName] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');

  // ── Вработување ───────────────────────────────────────────────────────────────
  const [appRole, setAppRole] = useState<Role>('trainer');
  const [employmentType, setEmploymentType] = useState('');
  const [hiredAt, setHiredAt] = useState('');
  const [locale, setLocale] = useState('');
  const [isActive, setIsActive] = useState(true);

  // ── Плата ─────────────────────────────────────────────────────────────────────
  const [grossSalary, setGrossSalary] = useState('');
  const [netSalary, setNetSalary] = useState('');
  const [iban, setIban] = useState('');

  // ── Белешки ─────────────────────────────────────────────────────────────────
  const [notes, setNotes] = useState('');

  const [error, setError] = useState<string | null>(null);

  // Prefill (edit) / reset (create) whenever the modal opens or the row changes.
  useEffect(() => {
    if (!open) return;
    setFirstName(staff?.first_name ?? '');
    setLastName(staff?.last_name ?? '');
    setEmail(staff?.email ?? '');
    setPhone(staff?.phone ?? '');
    setDateOfBirth(staff?.date_of_birth ?? '');
    setNationalId(staff?.national_id ?? '');
    setAddress(staff?.address ?? '');
    setEmergencyName(staff?.emergency_contact_name ?? '');
    setEmergencyPhone(staff?.emergency_contact_phone ?? '');
    setAppRole((staff?.app_role as Role) ?? 'trainer');
    setEmploymentType(staff?.employment_type ?? '');
    setHiredAt(staff?.hired_at ?? '');
    setLocale(staff?.locale ?? '');
    setIsActive(staff?.is_active ?? true);
    setGrossSalary(toMajor(staff?.gross_salary));
    setNetSalary(toMajor(staff?.net_salary));
    setIban(staff?.iban ?? '');
    setNotes(staff?.notes ?? '');
    setError(null);
  }, [open, staff]);

  async function submit(): Promise<void> {
    setError(null);
    if (!firstName || !lastName) {
      setError(t('staff.form.first_name'));
      return;
    }
    if (!email && !phone) {
      setError(t('errors.staff_requires_email_or_phone'));
      return; // do not call the API
    }
    const input = {
      firstName,
      lastName,
      email: email || null,
      phone: phone || null,
      appRole,
      locale: (locale || null) as 'mk' | 'sq' | 'en' | null,
      hiredAt: hiredAt || null,
      grossSalary: toMinor(grossSalary),
      netSalary: toMinor(netSalary),
      iban: iban || null,
      nationalId: nationalId || null,
      address: address || null,
      dateOfBirth: dateOfBirth || null,
      emergencyContactName: emergencyName || null,
      emergencyContactPhone: emergencyPhone || null,
      employmentType: employmentType || null,
      notes: notes || null,
    };
    try {
      if (isEdit && staff) {
        await update.mutateAsync({ id: staff.id, input: { ...input, isActive } as never });
      } else {
        await create.mutateAsync(input);
      }
      onOpenChange?.(false);
      onSaved?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!pending) onOpenChange?.(o);
      }}
      title={t(isEdit ? 'staff.form.edit_title' : 'staff.form.create_title')}
      size="lg"
      footer={
        <>
          <Button variant="secondary" disabled={pending} onClick={() => onOpenChange?.(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" loading={pending} onClick={() => void submit()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div
        role="group"
        aria-label={t(isEdit ? 'staff.form.edit_title' : 'staff.form.create_title')}
        className="grid max-h-[65vh] gap-5 overflow-y-auto pr-1"
      >
        {/* ── Личност ──────────────────────────────────────────────────────── */}
        <section className="grid gap-3">
          <h4 className="font-display text-sm font-semibold uppercase tracking-wide text-muted">
            {t('staff.form.section_person')}
          </h4>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('staff.form.first_name')} required>
              {(id) => (
                <Input
                  id={id}
                  aria-label="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  autoFocus
                />
              )}
            </Field>
            <Field label={t('staff.form.last_name')} required>
              {(id) => (
                <Input
                  id={id}
                  aria-label="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              )}
            </Field>
            <Field label={t('auth.email')}>
              {(id) => (
                <Input
                  id={id}
                  aria-label="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}
            </Field>
            <Field label={t('auth.phone')}>
              {(id) => (
                <Input
                  id={id}
                  aria-label="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              )}
            </Field>
            <Field label={t('staff.form.date_of_birth')}>
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  aria-label="dateOfBirth"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                />
              )}
            </Field>
            <Field label={t('staff.form.national_id')}>
              {(id) => (
                <Input
                  id={id}
                  aria-label="nationalId"
                  value={nationalId}
                  onChange={(e) => setNationalId(e.target.value)}
                />
              )}
            </Field>
            <Field label={t('staff.form.address')} className="sm:col-span-2">
              {(id) => (
                <Input
                  id={id}
                  aria-label="address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              )}
            </Field>
            <Field label={t('staff.form.emergency_contact_name')}>
              {(id) => (
                <Input
                  id={id}
                  aria-label="emergencyContactName"
                  value={emergencyName}
                  onChange={(e) => setEmergencyName(e.target.value)}
                />
              )}
            </Field>
            <Field label={t('staff.form.emergency_contact_phone')}>
              {(id) => (
                <Input
                  id={id}
                  aria-label="emergencyContactPhone"
                  value={emergencyPhone}
                  onChange={(e) => setEmergencyPhone(e.target.value)}
                />
              )}
            </Field>
          </div>
        </section>

        {/* ── Вработување ──────────────────────────────────────────────────── */}
        <section className="grid gap-3">
          <h4 className="font-display text-sm font-semibold uppercase tracking-wide text-muted">
            {t('staff.form.section_employment')}
          </h4>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('staff.form.role')}>
              {(id) => (
                <Select
                  id={id}
                  aria-label="appRole"
                  value={appRole}
                  onChange={(e) => setAppRole(e.target.value as Role)}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {t('staff.roles.' + r)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={t('staff.form.employment_type')}>
              {(id) => (
                <Select
                  id={id}
                  aria-label="employmentType"
                  value={employmentType}
                  onChange={(e) => setEmploymentType(e.target.value)}
                >
                  <option value="">—</option>
                  {EMPLOYMENT_TYPES.map((et) => (
                    <option key={et} value={et}>
                      {t('staff.employmentType.' + et)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={t('staff.form.hired_at')}>
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  aria-label="hiredAt"
                  value={hiredAt}
                  onChange={(e) => setHiredAt(e.target.value)}
                />
              )}
            </Field>
            <Field label={t('settings.language')}>
              {(id) => (
                <Select
                  id={id}
                  aria-label="locale"
                  value={locale}
                  onChange={(e) => setLocale(e.target.value)}
                >
                  <option value="">—</option>
                  {LOCALES.map((l) => (
                    <option key={l} value={l}>
                      {l.toUpperCase()}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          {isEdit ? (
            <label className="flex items-center gap-2 text-sm text-fg">
              <input
                type="checkbox"
                aria-label="isActive"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 rounded border-border accent-primary"
              />
              {t('common.active')}
            </label>
          ) : null}
        </section>

        {/* ── Плата ────────────────────────────────────────────────────────── */}
        <section className="grid gap-3">
          <h4 className="font-display text-sm font-semibold uppercase tracking-wide text-muted">
            {t('staff.form.section_salary')}
          </h4>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={`${t('staff.form.gross_salary')} (${t('common.currency')})`}>
              {(id) => (
                <Input
                  id={id}
                  type="number"
                  min="0"
                  step="0.01"
                  aria-label="grossSalary"
                  value={grossSalary}
                  onChange={(e) => setGrossSalary(e.target.value)}
                  placeholder="0.00"
                />
              )}
            </Field>
            <Field label={`${t('staff.form.net_salary')} (${t('common.currency')})`}>
              {(id) => (
                <Input
                  id={id}
                  type="number"
                  min="0"
                  step="0.01"
                  aria-label="netSalary"
                  value={netSalary}
                  onChange={(e) => setNetSalary(e.target.value)}
                  placeholder="0.00"
                />
              )}
            </Field>
            <Field label={t('staff.form.iban')} className="sm:col-span-2">
              {(id) => (
                <Input
                  id={id}
                  aria-label="iban"
                  value={iban}
                  onChange={(e) => setIban(e.target.value)}
                  className="font-mono"
                />
              )}
            </Field>
          </div>
        </section>

        {/* ── Белешки ──────────────────────────────────────────────────────── */}
        <section className="grid gap-3">
          <h4 className="font-display text-sm font-semibold uppercase tracking-wide text-muted">
            {t('staff.form.section_notes')}
          </h4>
          <Field label={t('staff.form.notes')}>
            {(id) => (
              <Textarea
                id={id}
                aria-label="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            )}
          </Field>
        </section>

        {error ? (
          <p role="alert" className="text-sm font-medium text-danger">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
